import {
    Component,
    Input,
    Output,
    EventEmitter,
    signal,
    computed,
    effect,
    inject,
    input,
    untracked,
    OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { IconModule } from '../../icon/icon.module';
import { PanicEventsService, PanicEventPayload } from '../../../services/api/panic-events.service';

export interface AlertPopupData {
    title: string;
    message: string;
    type: 'occurrence' | 'panic';
    entityId?: string;
    entityType?: string;
}

/**
 * Componente de popup de alerta (pânico, ocorrências, etc).
 *
 * Suporta fila de múltiplos popups simultâneos: ao receber uma nova notificação
 * de pânico, ela entra na fila em vez de substituir a anterior.
 *
 * Navegação:
 *   - Setas ◀ ▶ no header (visíveis se > 1 item) pulam entre itens.
 *     Quando o usuário navega, o índice interno muda — o pai não precisa saber.
 *   - Botão "Próximo" (visível se > 1 item) emite (next) — pai decide o que fazer.
 *   - "Fechar" consome o item ATUAL (pai remove do queue).
 *   - "Abrir" consome o item ATUAL + marca como lida + navega.
 *   - "Fechar todos" limpa a fila inteira.
 *
 * Quando a fila muda de tamanho (novo popup entra), o índice reseta pra 0 —
 * sempre mostra o item mais recente primeiro.
 *
 * API externa: o componente pai (NotificationsComponent) injeta uma fila
 * inteira via [queue]. Para simplificar a integração, aceitamos também
 * [data] (single) como atalho — equivale a uma fila de 1 item.
 *
 * Eventos:
 *   (close)     -> usuário fechou (sem marcar como lida)
 *   (next)      -> usuário pediu o próximo (sem marcar como lida)
 *   (previous)  -> usuário pediu voltar (sem marcar como lida)
 *   (open)      -> usuário abriu (marca como lida e navega)
 *   (closeAll)  -> usuário fechou todos os popups pendentes
 */
@Component({
    selector: 'app-alert-popup',
    standalone: true,
    imports: [CommonModule, IconModule],
    template: `
        @if (currentItem(); as item) {
            <div
                class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4"
                (click)="onBackdropClick()"
            >
                <div
                    class="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-hidden animate-slide-up flex flex-col"
                    [class]="getAlertClasses(item)"
                    (click)="$event.stopPropagation()"
                >
                    <!-- Header: ícone + título + setas de navegação -->
                    <div class="flex items-center gap-3 px-4 py-3 border-b border-gray-200">
                        <div
                            class="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
                            [class]="getIconClasses(item)"
                        >
                            @switch (getIconClass(item)) {
                                @case ('icon-security') {
                                    <icon-security class="text-base" />
                                }
                                @case ('icon-info-triangle') {
                                    <icon-info-triangle class="text-base" />
                                }
                            }
                        </div>
                        <h3 class="text-base font-semibold text-gray-900 flex-1 truncate">{{ item.title }}</h3>

                        @if (queueLength() > 1) {
                            <div class="flex items-center gap-1 shrink-0">
                                <button
                                    type="button"
                                    class="w-7 h-7 rounded-md flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                    [disabled]="currentIndex() === 0"
                                    (click)="onPrevious()"
                                    aria-label="Alerta anterior"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                                    </svg>
                                </button>
                                <span class="text-xs text-gray-500 tabular-nums w-10 text-center">
                                    {{ currentIndex() + 1 }}/{{ queueLength() }}
                                </span>
                                <button
                                    type="button"
                                    class="w-7 h-7 rounded-md flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                    [disabled]="currentIndex() === queueLength() - 1"
                                    (click)="onNext()"
                                    aria-label="Próximo alerta"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                                    </svg>
                                </button>
                            </div>
                        }
                    </div>

                    <!-- Body: mensagem + detalhes. Scroll em telas pequenas. -->
                    <div class="px-4 py-3 overflow-y-auto flex-1">
                        <p class="text-sm text-gray-700 leading-relaxed">{{ item.message }}</p>

                        <!-- Detalhes contextuais (só pra panicEvent) -->
                        @if (item.type === 'panic') {
                            <div class="mt-3 pt-3 border-t border-gray-100">
                                @if (loadingDetail()) {
                                    <div class="flex items-center gap-2 text-xs text-gray-400 italic">
                                        <div class="animate-spin h-3 w-3 border-2 border-gray-300 border-t-gray-500 rounded-full"></div>
                                        Carregando detalhes...
                                    </div>
                                }
                                @if (!loadingDetail() && detail(); as d) {
                                    <dl class="text-xs text-gray-600 space-y-1.5">
                                        @if (d.post?.name) {
                                            <div class="flex gap-2">
                                                <dt class="text-gray-400 shrink-0 w-24">Posto</dt>
                                                <dd class="font-medium text-gray-900 truncate">{{ d.post?.name }}</dd>
                                            </div>
                                        }
                                        @if (d.dateTime) {
                                            <div class="flex gap-2">
                                                <dt class="text-gray-400 shrink-0 w-24">Acionado</dt>
                                                <dd class="font-medium text-gray-900 truncate">{{ formatExact(d.dateTime) }}</dd>
                                            </div>
                                            <div class="flex gap-2">
                                                <dt class="text-gray-400 shrink-0 w-24">Há quanto tempo</dt>
                                                <dd class="font-medium text-gray-900">{{ formatRelative(d.dateTime, now()) }}</dd>
                                            </div>
                                        }
                                        @if (d.resident?.name) {
                                            <div class="flex gap-2">
                                                <dt class="text-gray-400 shrink-0 w-24">Quem</dt>
                                                <dd class="font-medium text-gray-900 truncate">{{ d.resident?.name }}</dd>
                                            </div>
                                        }
                                        @if (d.resident?.address) {
                                            <div class="flex gap-2">
                                                <dt class="text-gray-400 shrink-0 w-24">Endereço</dt>
                                                <dd class="text-gray-900">{{ d.resident?.address }}</dd>
                                            </div>
                                        }
                                    </dl>
                                }
                            </div>
                        }
                    </div>

                    <!-- Actions: botões compactos. Layout único pra qualquer tela. -->
                    <div class="flex flex-wrap justify-end gap-1.5 px-3 py-2 border-t border-gray-100 bg-gray-50">
                        @if (queueLength() > 1) {
                            <button
                                class="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-md transition-colors"
                                (click)="onCloseAll()"
                                type="button"
                            >
                                Fechar todos
                            </button>
                        }
                        <button
                            class="px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-200 rounded-md transition-colors"
                            (click)="onClose()"
                            type="button"
                        >
                            Fechar
                        </button>
                        @if (queueLength() > 1) {
                            <button
                                class="px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-200 rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                [disabled]="currentIndex() === 0"
                                (click)="onPrevious()"
                                type="button"
                            >
                                Voltar
                            </button>
                            <button
                                class="px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-200 rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                [disabled]="currentIndex() === queueLength() - 1"
                                (click)="onNext()"
                                type="button"
                            >
                                Próximo
                            </button>
                        }
                        @if (item.entityId) {
                            <button
                                class="px-4 py-1.5 text-sm font-semibold text-white rounded-md transition-colors"
                                [class]="getButtonClasses(item)"
                                (click)="onOpen()"
                                type="button"
                            >
                                Abrir
                            </button>
                        }
                    </div>
                </div>
            </div>
        }
    `,
    styles: [
        `
            @keyframes fadeUp {
                from { opacity: 0; transform: translateY(8px); }
                to { opacity: 1; transform: translateY(0); }
            }
            .animate-slide-up {
                animation: fadeUp 0.18s ease-out;
            }
        `,
    ],
})
export class AlertPopupComponent implements OnDestroy {
    /**
     * Fila completa de popups pendentes.
     *
     * Usa a nova sintaxe `input()` do Angular 17+: aceita signal do pai
     * ([queue]="alertPopupQueue") e reage automaticamente a mutações.
     */
    readonly queue = input<AlertPopupData[]>([]);

    /**
     * API legada: passar um único item. Mantido pra não quebrar consumidores
     * existentes que ainda usam [data].
     */
    @Input() set data(item: AlertPopupData | null) {
        if (item) {
            this._legacyQueue.set([item]);
        } else {
            this._legacyQueue.set([]);
        }
    }

    @Output() close = new EventEmitter<AlertPopupData | undefined>();
    @Output() next = new EventEmitter<AlertPopupData | undefined>();
    @Output() previous = new EventEmitter<void>();
    @Output() open = new EventEmitter<{ entityId: string; entityType: string }>();
    @Output() closeAll = new EventEmitter<void>();

    /** Fila interna (legado). Quando vazia, usa queue input. */
    private _legacyQueue = signal<AlertPopupData[]>([]);

    /**
     * Índice interno do item exibido. Navegação (◀ ▶, Próximo) muda este índice,
     * não a fila. Quando o pai atualiza a fila (novo item entra ou sai), um
     * effect reseta o índice pra 0 (mostra o item mais recente).
     */
    private readonly _currentIndex = signal(0);

    /**
     * Detalhe do panicEvent exibido atualmente (cache simples, sem TTL —
     * o backend é a fonte da verdade e invalidamos quando item muda).
     */
    private readonly _detail = signal<PanicEventPayload | null>(null);
    private readonly _loadingDetail = signal(false);

    /** Subscribe atual, cancelável quando item muda. */
    private _detailSub: { unsubscribe: () => void } | null = null;

    /** Service injetado pra buscar detalhe. */
    private readonly panicEventsService = inject(PanicEventsService);

    /** Detalhe público pra o template. */
    readonly detail = computed(() => this._detail());
    readonly loadingDetail = computed(() => this._loadingDetail());

    /**
     * Tick de "agora" pra atualizar o tempo relativo ("há quanto tempo")
     * em tempo real. Atualiza a cada 30s via setInterval.
     */
    private readonly _now = signal(Date.now());
    readonly now = computed(() => this._now());
    private _nowInterval: ReturnType<typeof setInterval> | null = null;

    /** Fila efetiva: prioriza queue input, fallback para legacy. */
    readonly effectiveQueue = computed<AlertPopupData[]>(() => {
        const input = this.queue();
        if (input.length > 0) return input;
        return this._legacyQueue();
    });

    /** Item atualmente exibido (controlado por _currentIndex). */
    readonly currentItem = computed<AlertPopupData | null>(() => {
        const q = this.effectiveQueue();
        if (q.length === 0) return null;
        const idx = Math.min(this._currentIndex(), q.length - 1);
        return q[idx] ?? null;
    });

    /** Índice atual limitado ao tamanho da fila (caso encolha). */
    readonly currentIndex = computed(() => {
        const q = this.effectiveQueue();
        if (q.length === 0) return 0;
        return Math.min(this._currentIndex(), q.length - 1);
    });

    readonly queueLength = computed(() => this.effectiveQueue().length);

    constructor() {
        // Efeito SIMPLES: reseta o índice sempre que a fila muda de tamanho
        // (novo popup chega ou um é removido pelo pai). untracked() evita
        // que a escrita crie loop.
        effect(() => {
            this.effectiveQueue();
            untracked(() => this._currentIndex.set(0));
        });

        // Efeito SIMPLES: rebusca detalhe sempre que o item ATUAL muda
        // (fila mudou OU navegação ◀/▶). Sem escrita em signals aqui —
        // a escrita é feita pelo callback do subscribe (async, fora do effect).
        effect(() => {
            // Lê explicitamente queue() E _currentIndex como dependências
            // pra garantir re-execução quando qualquer um dos dois muda.
            const q = this.queue();
            const idx = this._currentIndex();
            console.log('[AlertPopup detail-effect] queueLen=', q.length, 'idx=', idx);
            // Chama busca via função que cancela subscribe anterior e cria novo.
            untracked(() => this.carregarDetailAtual());
        });

        // Timer que atualiza "agora" a cada 30s pro campo "Há quanto tempo"
        // atualizar em tempo real.
        this._nowInterval = setInterval(() => {
            this._now.set(Date.now());
        }, 30_000);
    }

    ngOnDestroy(): void {
        if (this._nowInterval) {
            clearInterval(this._nowInterval);
        }
        if (this._detailSub) {
            this._detailSub.unsubscribe();
        }
    }

    /**
     * Carrega o detalhe do panicEvent do item atual.
     *
     * Cancelamento: se já existe subscribe em voo, cancela. Quando a resposta
     * chega, valida se o item atual ainda é o mesmo; senão, descarta.
     * Sem escritas em signals dentro de effect — a escrita (_detail.set) acontece
     * DENTRO do callback do subscribe (async, fora do effect).
     */
    private carregarDetailAtual(): void {
        // Cancela subscribe anterior
        if (this._detailSub) {
            this._detailSub.unsubscribe();
            this._detailSub = null;
        }

        const item = this.currentItem();
        const id = item?.entityId;
        const isPanic = item?.type === 'panic';

        if (!isPanic || !id) {
            this._detail.set(null);
            this._loadingDetail.set(false);
            return;
        }

        this._detail.set(null);
        this._loadingDetail.set(true);

        this._detailSub = this.panicEventsService.buscar(id).subscribe({
            next: (payload) => {
                // Se o usuário navegou pra outro popup, ignora resposta antiga
                if (this.currentItem()?.entityId !== id) return;
                this._detail.set(payload);
                this._loadingDetail.set(false);
                this._detailSub = null;
            },
            error: () => {
                if (this.currentItem()?.entityId !== id) return;
                this._loadingDetail.set(false);
                this._detailSub = null;
            },
        });
    }

    isVisible() {
        return this.currentItem() !== null;
    }

    getIconClass(item: AlertPopupData): 'icon-security' | 'icon-info-triangle' {
        return item.type === 'panic' ? 'icon-info-triangle' : 'icon-security';
    }

    /**
     * Formata data e hora exatas em pt-BR ("01/07/2026, 14:35").
     * Independente do "agora" — é o timestamp absoluto do evento.
     */
    formatExact(dateStr: string): string {
        const date = new Date(dateStr);
        const datePart = date.toLocaleDateString('pt-BR');
        const timePart = date.toLocaleTimeString('pt-BR', {
            hour: '2-digit',
            minute: '2-digit',
        });
        return `${datePart}, ${timePart}`;
    }

    /**
     * Formata tempo relativo em pt-BR ("agora", "há 2 min", "há 1 h", "há 3 d").
     * Recebe `now` como parametro pra permitir signal reativo (atualiza
     * automaticamente a cada 30s).
     */
    formatRelative(dateStr: string, now: number = Date.now()): string {
        const date = new Date(dateStr).getTime();
        const diffMs = now - date;
        const diffMinutes = Math.floor(diffMs / (1000 * 60));
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        if (diffMinutes < 1) return 'agora';
        if (diffMinutes < 60) return `há ${diffMinutes} min`;
        if (diffHours < 24) return `há ${diffHours} h`;
        if (diffDays < 7) return `há ${diffDays} d`;
        return new Date(date).toLocaleDateString('pt-BR');
    }

    getAlertClasses(item: AlertPopupData) {
        if (item.type === 'panic') {
            return 'border-4 border-red-500 shadow-red-200';
        }
        return 'border-4 border-amber-500 shadow-amber-200';
    }

    getIconClasses(item: AlertPopupData) {
        if (item.type === 'panic') {
            return 'bg-red-100 text-red-600 ring-4 ring-red-200';
        }
        return 'bg-amber-100 text-amber-600 ring-4 ring-amber-200';
    }

    getButtonClasses(item: AlertPopupData) {
        if (item.type === 'panic') {
            return 'bg-red-600 hover:bg-red-700 focus:ring-red-300 shadow-lg shadow-red-200';
        }
        return 'bg-amber-600 hover:bg-amber-700 focus:ring-amber-300 shadow-lg shadow-amber-200';
    }

    onClose() {
        const current = this.currentItem();
        this.close.emit(current ?? undefined);
    }

    onNext() {
        // Avança o índice localmente; emite (next) só pra notificar o pai.
        this._currentIndex.update(i => Math.min(i + 1, this.effectiveQueue().length - 1));
        const current = this.currentItem();
        this.next.emit(current ?? undefined);
    }

    onPrevious() {
        // Volta o índice localmente; emite (previous) só pra notificar o pai.
        this._currentIndex.update(i => Math.max(i - 1, 0));
        this.previous.emit();
    }

    onCloseAll() {
        this.closeAll.emit();
    }

    onOpen() {
        const item = this.currentItem();
        if (item?.entityId && item?.entityType) {
            this.open.emit({
                entityId: item.entityId,
                entityType: item.entityType,
            });
        }
    }

    onBackdropClick() {
        // Mesmo comportamento de fechar — usuário pode dispensar clicando fora
        this.onClose();
    }
}
